import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Building2, User, MapPin, Mail, Phone, FileText,
  Receipt, CreditCard, Edit2, CheckCircle, AlertCircle, 
  Briefcase, Scale, Hash, Calendar, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantProfilePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [advocateProfile, setAdvocateProfile] = useState(null);
  const [billingProfile, setBillingProfile] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [walletInfo, setWalletInfo] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch all data in parallel
      const [advocateRes, billingRes, userRes, walletRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/tenant/advocate-profile`, { headers }),
        axios.get(`${API_URL}/api/invoice/billing-profile`, { headers }),
        axios.get(`${API_URL}/api/auth/me`, { headers }),
        axios.get(`${API_URL}/api/wallet`, { headers })
      ]);

      if (advocateRes.status === 'fulfilled') {
        setAdvocateProfile(advocateRes.value.data?.profile || null);
      }

      if (billingRes.status === 'fulfilled') {
        setBillingProfile(billingRes.value.data || null);
      }

      if (userRes.status === 'fulfilled') {
        setUserInfo(userRes.value.data || null);
      }

      if (walletRes.status === 'fulfilled') {
        setWalletInfo(walletRes.value.data?.wallet || walletRes.value.data || null);
      }

    } catch (err) {
      console.error('Error fetching tenant data:', err);
      toast.error('Failed to load some tenant information');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '₹0.00';
    return `₹${parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tenant profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Tenant Profile</h1>
                  <p className="text-sm text-gray-500">View and manage your organization details</p>
                </div>
              </div>
            </div>
            
            <button
              onClick={fetchAllData}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto py-6 px-4 space-y-6">
        
        {/* User Information Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <User className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Account Information</h2>
                  <p className="text-blue-100 text-sm">Your login and account details</p>
                </div>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Username</label>
                <p className="text-gray-900 font-medium">{userInfo?.username || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Full Name</label>
                <p className="text-gray-900 font-medium">{userInfo?.full_name || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Role</label>
                <p className="text-gray-900 font-medium capitalize">{userInfo?.role || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Tenant ID</label>
                <p className="text-gray-600 font-mono text-sm">{userInfo?.tenant_id || 'default'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Wallet Information Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <CreditCard className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Wallet Balance</h2>
                  <p className="text-green-100 text-sm">Your current credit balance</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">{formatCurrency(walletInfo?.balance)}</p>
                <p className="text-green-100 text-sm">Available Credits</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/invoices')}
                className="px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors flex items-center text-sm"
              >
                <Receipt className="w-4 h-4 mr-2" />
                View Invoices
              </button>
              <button
                onClick={() => navigate('/wallet/transactions')}
                className="px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex items-center text-sm"
              >
                <FileText className="w-4 h-4 mr-2" />
                Transaction History
              </button>
            </div>
          </div>
        </div>

        {/* Advocate Profile Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Scale className="w-5 h-5 text-gray-600" />
                <div>
                  <h2 className="text-lg font-medium text-gray-900">Advocate Profile</h2>
                  <p className="text-sm text-gray-500">Used in document headers</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/settings/advocate-profile')}
                className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </button>
            </div>
          </div>
          <div className="p-6">
            {advocateProfile ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Advocate Name</label>
                  <p className="text-gray-900 font-medium text-lg">{advocateProfile.advocate_name || '-'}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Address</label>
                  <p className="text-gray-700">{advocateProfile.address || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Phone</label>
                  <p className="text-gray-700">{advocateProfile.phone || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Email</label>
                  <p className="text-gray-700">{advocateProfile.email || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Enrollment No.</label>
                  <p className="text-gray-700 font-mono">{advocateProfile.enrollment_number || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">PAN</label>
                  <p className="text-gray-700 font-mono">{advocateProfile.pan_number || '-'}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
                <p className="text-gray-600 mb-3">Advocate profile not configured</p>
                <button
                  onClick={() => navigate('/settings/advocate-profile')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  Set Up Profile
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Billing Profile Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Receipt className="w-5 h-5 text-gray-600" />
                <div>
                  <h2 className="text-lg font-medium text-gray-900">Billing Profile</h2>
                  <p className="text-sm text-gray-500">Used for GST invoices</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/settings/billing-profile')}
                className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </button>
            </div>
          </div>
          <div className="p-6">
            {billingProfile ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Legal Name</label>
                  <p className="text-gray-900 font-medium text-lg">{billingProfile.legal_name || '-'}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Billing Address</label>
                  <p className="text-gray-700">{billingProfile.billing_address || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">State</label>
                  <p className="text-gray-700">
                    {billingProfile.state || '-'}
                    {billingProfile.state_code && (
                      <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded">
                        Code: {billingProfile.state_code}
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Email</label>
                  <p className="text-gray-700">{billingProfile.email || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">GSTIN</label>
                  <p className="text-gray-700 font-mono">
                    {billingProfile.gstin || (
                      <span className="text-gray-400 italic">Not provided</span>
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">PAN</label>
                  <p className="text-gray-700 font-mono">
                    {billingProfile.pan || (
                      <span className="text-gray-400 italic">Not provided</span>
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
                <p className="text-gray-600 mb-3">Billing profile not configured</p>
                <p className="text-sm text-gray-500 mb-4">Required for generating GST invoices on wallet recharge</p>
                <button
                  onClick={() => navigate('/settings/billing-profile')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  Set Up Billing Profile
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button
              onClick={() => navigate('/settings/advocate-profile')}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center"
            >
              <Scale className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <span className="text-sm text-gray-700">Advocate Profile</span>
            </button>
            <button
              onClick={() => navigate('/settings/billing-profile')}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center"
            >
              <Receipt className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <span className="text-sm text-gray-700">Billing Profile</span>
            </button>
            <button
              onClick={() => navigate('/invoices')}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center"
            >
              <FileText className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <span className="text-sm text-gray-700">View Invoices</span>
            </button>
            <button
              onClick={() => navigate('/wallet/transactions')}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center"
            >
              <CreditCard className="w-6 h-6 text-amber-600 mx-auto mb-2" />
              <span className="text-sm text-gray-700">Transactions</span>
            </button>
          </div>
        </div>

      </main>
    </div>
  );
};

export default TenantProfilePage;
